import type { Scene } from '@babylonjs/core/scene';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { InstancedMesh } from '@babylonjs/core/Meshes/instancedMesh';
import '@babylonjs/core/Meshes/instancedMesh';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateDisc } from '@babylonjs/core/Meshes/Builders/discBuilder';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import type { CraneLoadRig, HazardsConfig } from '../config/hazards.config';
import type { WorldConfig } from '../config/world.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import { laneCenterX } from '../level/segments/segmentBuilders';
import { CABLE_REEL_SCALE } from '../obstacles/ObstacleCatalog';
import type { TrackPresenter } from '../level/TrackPresenter';
import type { EntityVisual } from '../rendering/EntityVisual';
import type { VisualPool } from '../rendering/VisualPool';
import { clamp01, lerp, TAU } from '../utils/math';
import { craneSwingRadius, type CraneLoadPose, type CraneSwingPose } from './CraneLoadModels';
import type { ExcavatorPose } from './ExcavatorModel';
import { HazardKind, VehicleType, type CraneMount, type HazardDefinition } from './HazardDefinition';
import type { HazardInstance } from './HazardInstance';
import { HazardPhase, lerpPose, type HazardPose } from './HazardModel';
import type { VehiclePose } from './VehicleModel';

/**
 * Rig facts of the Blender exports (public/assets/models, node names `<ASSET>_PIVOT_*`).
 * Pivots below the GLB root live in glTF space: the loader mirrors x, so turns about y run the
 * other way round (×−1); turns about x (pitch) and moves along y/z are unchanged.
 */
const GLTF_YAW = -1;
const RIG = {
  craneLoadA: { asset: 'OBS_CRANE_LOAD_A', swing: 'OBS_CRANE_LOAD_A_PIVOT_SWING' },
  craneLoadB: { asset: 'OBS_CRANE_LOAD_B', swing: 'OBS_CRANE_LOAD_B_PIVOT_SWING' },
  crane: {
    asset: 'VEH_CRANE_A',
    trolley: 'VEH_CRANE_A_PIVOT_TROLLEY',
    hook: 'VEH_CRANE_A_PIVOT_HOOK',
    ropes: 'VEH_CRANE_A_ROPES',
    /** Trolley track above the ground (slew pivot 20.5 + trolley 0.8). */
    trolleyHeight: 21.3,
    /** Hook below / hook block above its pivot; rope mesh length at scale 1. */
    hookBottom: 1.34,
    hookBlockTop: 0.55,
    ropeLength: 8.7,
  },
  excavator: {
    asset: 'VEH_EXCAVATOR_A',
    upper: 'VEH_EXCAVATOR_A_PIVOT_UPPER',
    boom: 'VEH_EXCAVATOR_A_PIVOT_BOOM',
    stick: 'VEH_EXCAVATOR_A_PIVOT_STICK',
    bucket: 'VEH_EXCAVATOR_A_PIVOT_BUCKET',
    /** Beacon on the cab roof (glTF space of the upper carriage). */
    beacon: new Vector3(0.95, 2.28, 0.9),
    heap: 'OBS_RUBBLE_HEAP_A',
  },
  forklift: {
    asset: 'VEH_FORKLIFT_A',
    mast: 'VEH_FORKLIFT_A_PIVOT_MAST',
    forks: 'VEH_FORKLIFT_A_PIVOT_FORKS',
    load: 'VEH_FORKLIFT_A_PIVOT_LOAD',
    wheels: { FL: 0.33, FR: 0.33, RL: 0.27, RR: 0.27 } as Readonly<Record<string, number>>,
    /** Rear-wheel steering. */
    steered: ['RL', 'RR'],
    steerSign: -1,
    /** Roof beacon (vehicle space, x already mirrored). */
    beacons: [new Vector3(-0.3, 2.33, -0.76)],
  },
  cableReel: {
    asset: 'OBS_CABLE_REEL_A',
    /** Drum radius at its game size (Ø 1.6 m model × CABLE_REEL_SCALE). */
    radius: 0.8 * CABLE_REEL_SCALE,
  },
  truck: {
    asset: 'VEH_TRUCK_A',
    wheels: { FL: 0.56, FR: 0.56, RL1: 0.56, RL2: 0.56, RR1: 0.56, RR2: 0.56 } as Readonly<Record<string, number>>,
    steered: ['FL', 'FR'],
    steerSign: 1,
    beacons: [new Vector3(-0.84, 3.49, 3.9), new Vector3(0.84, 3.49, 3.9)],
  },
} as const;

/** Look of the warning helpers (presentation only). */
const LOOK = {
  beacon: { diameter: 0.22, segments: 8, color: new Color3(1, 0.55, 0.05) },
  /** Polygon offset of the flat helpers (pulled towards the camera, no z-fighting with the road). */
  zOffset: -2,
  shadow: { alpha: 0.32, height: 0.02, tessellation: 32 },
  /**
   * Falling rubble chunks: `selfLight` keeps them readable in the shade of the bucket; each chunk falls
   * for `fall` of the dump (staggered over the other half) and tumbles by `tumble` radians.
   */
  debris: { size: 0.28, color: new Color3(0.5, 0.46, 0.4), spread: 0.6, selfLight: 0.45, fall: 0.5, tumble: new Vector3(5, 3, 4) },
  /** Heap visual while it is still growing (x/z scale at 0 % of the pile). */
  heapStartWidth: 0.45,
  /** Zero scale is degenerate – a pile that has not landed yet is hidden instead. */
  minScale: 0.01,
  /** Beacon flash rate multiplier while a vehicle signals a lane change. */
  signalFlashFactor: 2,
} as const;

/** Original local transform of a rig node – restored before the visual goes back to the pool. */
interface NodeState {
  readonly node: TransformNode;
  readonly position: Vector3;
  readonly rotation: Quaternion | null;
  readonly scaling: Vector3;
  readonly enabled: boolean;
}

interface Entry {
  readonly hazard: HazardInstance;
  readonly main: EntityVisual;
  readonly crane: EntityVisual | null;
  readonly heap: EntityVisual | null;
  readonly shadow: InstancedMesh | null;
  readonly beacons: InstancedMesh[];
  readonly debris: InstancedMesh[];
  /** Touched rig nodes by pivot name (main visual and crane). */
  readonly nodes: Map<string, NodeState>;
  readonly pose: HazardPose;
  /** Rolling cable drum: heading node on the road and the axle node it rolls about (the drum hangs below it). */
  readonly roll: { readonly heading: TransformNode; readonly axle: TransformNode } | null;
}

/**
 * Visual side of the moving hazards: pooled GLB visuals whose rig pivots follow the engine-free
 * models (interpolated between fixed steps), the crane that carries a load (its jib and trolley follow
 * a swinging block's rope top), warning beacons, soft shadows under crane loads, the growing rubble
 * heap with falling debris. Gameplay colliders live in the models – nothing here affects collision.
 */
export class HazardPresenter {
  private readonly entries = new Map<number, Entry>();
  private readonly beaconSource: Mesh;
  private readonly shadowSource: Mesh;
  private readonly debrisSource: Mesh;
  private readonly scratch = new Quaternion();
  private readonly twist = new Quaternion();

  constructor(
    private readonly scene: Scene,
    private readonly pool: VisualPool,
    private readonly track: TrackPresenter,
    private readonly config: HazardsConfig,
    private readonly world: WorldConfig,
    events: EventBus<GameEvents>,
  ) {
    const container = new TransformNode('__HAZARD_FX_SOURCES__', scene);
    this.beaconSource = this.source(CreateSphere('FX_HAZARD_BEACON', { diameter: LOOK.beacon.diameter, segments: LOOK.beacon.segments }, scene), container, glow(scene, 'MAT_FX_BEACON', LOOK.beacon.color, 1));
    this.shadowSource = this.source(flatDisc(scene, 'FX_HAZARD_SHADOW'), container, glow(scene, 'MAT_FX_HAZARD_SHADOW', Color3.Black(), LOOK.shadow.alpha));
    const debrisMaterial = new StandardMaterial('MAT_FX_DEBRIS', scene);
    debrisMaterial.diffuseColor = LOOK.debris.color;
    debrisMaterial.emissiveColor = LOOK.debris.color.scale(LOOK.debris.selfLight);
    debrisMaterial.specularColor = Color3.Black();
    this.debrisSource = this.source(CreateBox('FX_HAZARD_DEBRIS', { size: LOOK.debris.size }, scene), container, debrisMaterial);

    events.on('hazard:spawned', ({ hazard }) => this.show(hazard));
    events.on('hazard:despawned', ({ hazard }) => this.hide(hazard));
    events.on('hazard:spared', ({ hazard }) => this.spare(hazard));
  }

  /** Per frame: poses between the last two fixed steps (`alpha`), blinking by render time. */
  render(alpha: number, time: number): void {
    const { beaconFlashHz } = this.config.presentation;
    const flash = Math.sin(TAU * beaconFlashHz * time) > 0;
    // A vehicle about to change lanes flashes twice as fast.
    const signal = Math.sin(TAU * LOOK.signalFlashFactor * beaconFlashHz * time) > 0;
    for (const entry of this.entries.values()) {
      const model = entry.hazard.model;
      switch (model.kind) {
        case HazardKind.CraneBeam:
        case HazardKind.CraneBlock:
          this.renderCraneLoad(entry, lerpPose(model.previous, model.pose, alpha, entry.pose as CraneLoadPose));
          break;
        case HazardKind.CraneSwing:
          this.renderCraneSwing(entry, lerpPose(model.previous, model.pose, alpha, entry.pose as CraneSwingPose));
          break;
        case HazardKind.Excavator:
          this.renderExcavator(entry, lerpPose(model.previous, model.pose, alpha, entry.pose as ExcavatorPose));
          break;
        case HazardKind.Vehicle:
          this.renderVehicle(entry, lerpPose(model.previous, model.pose, alpha, entry.pose as VehiclePose));
          break;
      }
      const warning = model.phase !== HazardPhase.Idle;
      const on = model.kind === HazardKind.Vehicle && model.signalling ? signal : flash;
      for (const beacon of entry.beacons) beacon.setEnabled(warning && on);
    }
  }

  // ── Kinds ────────────────────────────────────────────────────────────────

  private renderCraneLoad(entry: Entry, pose: CraneLoadPose): void {
    const definition = entry.hazard.definition;
    if (definition.kind !== HazardKind.CraneBeam && definition.kind !== HazardKind.CraneBlock) return;
    const beam = definition.kind === HazardKind.CraneBeam;
    const rig = beam ? this.config.craneBeam.rig : this.config.craneBlock.rig;
    const x = beam ? 0 : laneCenterX(this.world, definition.lane);
    const rootY = pose.hoist - rig.loadBottom;
    entry.main.root.position.set(x, rootY, definition.z);
    // Positive model swing = load towards +z: a turn about x moves what hangs below towards −z.
    this.turn(entry, beam ? RIG.craneLoadA.swing : RIG.craneLoadB.swing, GLTF_YAW * pose.spin, -pose.swing);
    if (entry.crane) this.renderCrane(entry, definition.crane, x, definition.z, rootY + rig.pivotHeight);
    const swingRadius = rig.pivotHeight - rig.loadBottom - rig.load.height / 2;
    this.renderShadow(entry, x, definition.z + swingRadius * Math.sin(pose.swing), rig);
  }

  /**
   * Swinging block: the rope top hangs under the trolley (which works the swing a little), the block
   * swings sideways on its sling about it, twisting slightly – the same rotation as the collider.
   */
  private renderCraneSwing(entry: Entry, pose: CraneSwingPose): void {
    const definition = entry.hazard.definition;
    if (definition.kind !== HazardKind.CraneSwing) return;
    const rig = this.config.craneSwing.rig;
    const rootY = pose.hoist - rig.loadBottom;
    entry.main.root.position.set(pose.pivotX, rootY, definition.z);
    this.pendulum(entry, RIG.craneLoadB.swing, pose);
    if (entry.crane) this.renderCrane(entry, definition.crane, pose.pivotX, definition.z, rootY + rig.pivotHeight);
    const radius = craneSwingRadius(rig);
    const loadX = pose.pivotX + radius * Math.sin(pose.lateral);
    const loadZ = definition.z + radius * Math.cos(pose.lateral) * Math.sin(pose.swing);
    this.renderShadow(entry, loadX, loadZ, rig);
  }

  /** Crane beside the track: jib turned over the load, trolley above it, hook on the load's rope. */
  private renderCrane(entry: Entry, mount: CraneMount, x: number, z: number, ropeTop: number): void {
    const crane = entry.crane;
    if (!crane) return;
    const dx = x - mount.x;
    const dz = z - mount.z;
    crane.root.position.set(mount.x, 0, mount.z);
    crane.root.rotation.y = Math.atan2(dx, dz);
    const c = RIG.crane;
    const hookY = ropeTop + c.hookBottom - c.trolleyHeight;
    this.move(entry, c.trolley, 'z', Math.hypot(dx, dz));
    this.move(entry, c.hook, 'y', hookY);
    this.stretch(entry, c.ropes, Math.max(LOOK.minScale, -(hookY + c.hookBlockTop) / c.ropeLength));
  }

  private renderExcavator(entry: Entry, pose: ExcavatorPose): void {
    const definition = entry.hazard.definition;
    if (definition.kind !== HazardKind.Excavator) return;
    const e = RIG.excavator;
    this.turn(entry, e.upper, GLTF_YAW * pose.slew, 0);
    this.turn(entry, e.boom, 0, pose.boom);
    this.turn(entry, e.stick, 0, pose.stick);
    this.turn(entry, e.bucket, 0, pose.bucket);

    const heap = entry.heap;
    if (heap) {
      const visible = pose.heap > 0 && !entry.hazard.spared;
      heap.root.setEnabled(visible);
      const width = lerp(LOOK.heapStartWidth, 1, pose.heap);
      heap.root.scaling.set(width, Math.max(LOOK.minScale, pose.heap), width);
    }
    this.renderDebris(entry, pose.dump, laneCenterX(this.world, definition.lane), definition.z);
  }

  /** Chunks fall from the bucket onto the heap one after another while it dumps. */
  private renderDebris(entry: Entry, dump: number, heapX: number, heapZ: number): void {
    const bucket = entry.hazard.model.colliders[0]?.box;
    const count = entry.debris.length;
    entry.debris.forEach((chunk, i) => {
      const { fall, tumble } = LOOK.debris;
      const start = (i / count) * (1 - fall);
      const t = clamp01((dump - start) / fall);
      const falling = dump > 0 && t > 0 && t < 1 && bucket !== undefined;
      chunk.setEnabled(falling);
      if (!falling || !bucket) return;
      const jitter = (i / count - 0.5) * LOOK.debris.spread;
      const fromX = (bucket.minX + bucket.maxX) / 2 + jitter;
      const fromZ = (bucket.minZ + bucket.maxZ) / 2 - jitter;
      chunk.position.set(lerp(fromX, heapX + jitter, t), lerp(bucket.minY, LOOK.debris.size, t * t), lerp(fromZ, heapZ - jitter, t));
      chunk.rotation.set(t * tumble.x + i, t * tumble.y, t * tumble.z);
    });
  }

  private renderVehicle(entry: Entry, pose: VehiclePose): void {
    const definition = entry.hazard.definition;
    if (definition.kind !== HazardKind.Vehicle) return;
    if (entry.roll) {
      // rolls towards the runner: about its own axle, turned a little into a lane change
      entry.roll.heading.position.set(pose.x, definition.elevation ?? 0, pose.z);
      entry.roll.heading.rotation.y = pose.yaw + pose.steer;
      entry.roll.axle.rotation.x = pose.travelled / RIG.cableReel.radius;
      return;
    }
    entry.main.root.position.set(pose.x, 0, pose.z);
    entry.main.root.rotation.y = pose.yaw;
    const rig = definition.vehicle === VehicleType.Truck ? RIG.truck : RIG.forklift;
    const asset = rig.asset;
    for (const [wheel, radius] of Object.entries(rig.wheels)) {
      const steered = (rig.steered as readonly string[]).includes(wheel);
      this.turn(entry, `${asset}_PIVOT_WHEEL_${wheel}`, steered ? rig.steerSign * pose.steer : 0, pose.travelled / radius);
    }
    if (definition.vehicle === VehicleType.Forklift) {
      const f = RIG.forklift;
      this.turn(entry, f.mast, 0, -pose.tilt);
      this.move(entry, f.forks, 'y', pose.forks, true);
      entry.nodes.get(f.load)?.node.setEnabled(definition.load === true);
    }
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  private show(hazard: HazardInstance): void {
    const parent = this.track.rootOf(hazard.segment);
    const definition = hazard.definition;
    const main = this.pool.acquire(assetOf(definition), parent);
    const usesCrane = definition.kind === HazardKind.CraneBeam || definition.kind === HazardKind.CraneBlock || definition.kind === HazardKind.CraneSwing;
    const crane = usesCrane ? this.pool.acquire(RIG.crane.asset, parent) : null;
    const heap = definition.kind === HazardKind.Excavator ? this.pool.acquire(RIG.excavator.heap, parent) : null;
    if (definition.kind === HazardKind.Excavator) {
      main.root.position.set(definition.machine.x, 0, definition.machine.z);
      heap?.root.position.set(laneCenterX(this.world, definition.lane), 0, definition.z);
      heap?.root.setEnabled(false);
    }

    let roll: Entry['roll'] = null;
    if (definition.kind === HazardKind.Vehicle && definition.vehicle === VehicleType.CableReel) {
      const heading = new TransformNode(`HAZ_ROLL_${hazard.uid}`, this.scene);
      heading.parent = parent;
      const axle = new TransformNode(`HAZ_ROLL_AXLE_${hazard.uid}`, this.scene);
      axle.parent = heading;
      axle.position.y = RIG.cableReel.radius;
      main.root.parent = axle;
      main.root.position.set(0, -RIG.cableReel.radius, 0);
      main.root.rotation.set(0, 0, 0);
      main.root.scaling.setAll(CABLE_REEL_SCALE);
      roll = { heading, axle };
    }

    const nodes = new Map<string, NodeState>();
    for (const visual of [main, crane]) {
      if (!visual) continue;
      for (const node of visual.root.getChildTransformNodes(false)) {
        const name = node.name.slice(node.name.lastIndexOf(':') + 1);
        if (/_PIVOT_|_ROPES$/.test(name)) nodes.set(name, nodeState(node));
      }
    }

    const entry: Entry = {
      hazard,
      main,
      crane,
      heap,
      shadow: usesCrane ? this.instance(this.shadowSource, parent) : null,
      beacons: this.beaconsFor(definition, main, nodes),
      debris: definition.kind === HazardKind.Excavator ? Array.from({ length: this.config.presentation.debrisChunks }, () => this.instance(this.debrisSource, parent)) : [],
      nodes,
      pose: { ...hazard.model.pose },
      roll,
    };
    this.entries.set(hazard.uid, entry);
  }

  private hide(hazard: HazardInstance): void {
    const entry = this.entries.get(hazard.uid);
    if (!entry) return;
    for (const mesh of [entry.shadow, ...entry.beacons, ...entry.debris]) mesh?.dispose();
    // Pooled visuals are shared with decorations (parked forklift, backdrop crane): restore the rig.
    for (const state of entry.nodes.values()) restore(state);
    entry.main.root.setEnabled(true);
    if (entry.roll) {
      // the pooled drum is shared with the standing obstacles: back under the segment root, upright
      entry.main.root.parent = entry.roll.heading.parent;
      entry.main.root.rotation.set(0, 0, 0);
      entry.roll.heading.dispose();
    }
    for (const visual of [entry.main, entry.crane, entry.heap]) if (visual) this.pool.release(visual);
    this.entries.delete(hazard.uid);
  }

  /** Got past with protection or a life: vehicles and blocks are gone (like smashed obstacles), the heap too. */
  private spare(hazard: HazardInstance): void {
    const entry = this.entries.get(hazard.uid);
    if (!entry) return;
    switch (hazard.definition.kind) {
      case HazardKind.Vehicle:
      case HazardKind.CraneBlock:
      case HazardKind.CraneSwing:
        entry.main.root.setEnabled(false);
        entry.shadow?.setEnabled(false);
        for (const beacon of entry.beacons) beacon.dispose();
        entry.beacons.length = 0;
        break;
      case HazardKind.Excavator:
        entry.heap?.root.setEnabled(false);
        break;
      case HazardKind.CraneBeam:
        break; // the runner ducked under it – the beams stay
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private beaconsFor(definition: HazardDefinition, main: EntityVisual, nodes: Map<string, NodeState>): InstancedMesh[] {
    switch (definition.kind) {
      case HazardKind.Vehicle: {
        if (definition.vehicle === VehicleType.CableReel) return []; // nobody drives it – no beacon
        const rig = definition.vehicle === VehicleType.Truck ? RIG.truck : RIG.forklift;
        return rig.beacons.map((p) => {
          const beacon = this.instance(this.beaconSource, main.root);
          beacon.position.copyFrom(p);
          return beacon;
        });
      }
      case HazardKind.Excavator: {
        // On the cab roof – slews with the upper carriage (glTF space of the pivot).
        const upper = nodes.get(RIG.excavator.upper)?.node ?? main.root;
        const beacon = this.instance(this.beaconSource, upper);
        beacon.position.copyFrom(RIG.excavator.beacon);
        return [beacon];
      }
      default:
        return [];
    }
  }

  /** Soft shadow on the road right under the load (follows the swing) – shows where it hangs. */
  private renderShadow(entry: Entry, x: number, z: number, rig: CraneLoadRig): void {
    const shadow = entry.shadow;
    if (!shadow) return;
    const kind = entry.hazard.definition.kind;
    const gone = entry.hazard.spared && (kind === HazardKind.CraneBlock || kind === HazardKind.CraneSwing);
    shadow.setEnabled(!gone);
    shadow.position.set(x, LOOK.shadow.height, z);
    shadow.scaling.set(rig.load.width / 2, 1, rig.load.depth);
  }

  /** Yaw (about y) and pitch (about x) of a rig node relative to its rest rotation. */
  private turn(entry: Entry, pivot: string, yaw: number, pitch: number): void {
    const state = entry.nodes.get(pivot);
    if (!state) return; // greybox fallback without rig
    const node = state.node;
    Quaternion.RotationYawPitchRollToRef(yaw, pitch, 0, this.scratch);
    node.rotationQuaternion ??= new Quaternion();
    if (state.rotation) state.rotation.multiplyToRef(this.scratch, node.rotationQuaternion);
    else node.rotationQuaternion.copyFrom(this.scratch);
  }

  /**
   * Pendulum about a rope top: turn about the rope (spin) first, then tilt sideways (lateral, about z) and
   * along the track (swing, about x) – craneSwingBox uses the same order. glTF space mirrors x: turns about
   * y and z run the other way round.
   */
  private pendulum(entry: Entry, pivot: string, pose: CraneSwingPose): void {
    const state = entry.nodes.get(pivot);
    if (!state) return; // greybox fallback without rig
    Quaternion.RotationYawPitchRollToRef(0, -pose.swing, GLTF_YAW * pose.lateral, this.scratch);
    Quaternion.RotationYawPitchRollToRef(GLTF_YAW * pose.spin, 0, 0, this.twist);
    this.scratch.multiplyInPlace(this.twist);
    const node = state.node;
    node.rotationQuaternion ??= new Quaternion();
    if (state.rotation) state.rotation.multiplyToRef(this.scratch, node.rotationQuaternion);
    else node.rotationQuaternion.copyFrom(this.scratch);
  }

  /** Moves a rig node along one axis: to `value`, or by `value` from its rest position. */
  private move(entry: Entry, pivot: string, axis: 'y' | 'z', value: number, relative = false): void {
    const state = entry.nodes.get(pivot);
    if (!state) return;
    state.node.position[axis] = relative ? state.position[axis] + value : value;
  }

  private stretch(entry: Entry, pivot: string, factor: number): void {
    const state = entry.nodes.get(pivot);
    if (state) state.node.scaling.y = state.scaling.y * factor;
  }

  private source(mesh: Mesh, container: TransformNode, material: StandardMaterial): Mesh {
    mesh.material = material;
    mesh.parent = container;
    mesh.isPickable = false;
    mesh.setEnabled(false);
    return mesh;
  }

  private instance(source: Mesh, parent: TransformNode): InstancedMesh {
    const instance = source.createInstance(`${source.name}_${this.entries.size}`);
    instance.parent = parent;
    instance.isPickable = false;
    instance.setEnabled(false);
    return instance;
  }
}

function assetOf(definition: HazardDefinition): string {
  switch (definition.kind) {
    case HazardKind.CraneBeam:
      return RIG.craneLoadA.asset;
    case HazardKind.CraneBlock:
    case HazardKind.CraneSwing:
      return RIG.craneLoadB.asset;
    case HazardKind.Excavator:
      return RIG.excavator.asset;
    case HazardKind.Vehicle:
      if (definition.vehicle === VehicleType.CableReel) return RIG.cableReel.asset;
      return definition.vehicle === VehicleType.Truck ? RIG.truck.asset : RIG.forklift.asset;
  }
}

function nodeState(node: TransformNode): NodeState {
  return {
    node,
    position: node.position.clone(),
    rotation: node.rotationQuaternion ? node.rotationQuaternion.clone() : null,
    scaling: node.scaling.clone(),
    enabled: node.isEnabled(false),
  };
}

function restore(state: NodeState): void {
  state.node.position.copyFrom(state.position);
  if (state.rotation) (state.node.rotationQuaternion ??= new Quaternion()).copyFrom(state.rotation);
  else state.node.rotationQuaternion = null;
  state.node.scaling.copyFrom(state.scaling);
  state.node.setEnabled(state.enabled);
}

/** Unlit emissive material (beacons, soft shadow). */
function glow(scene: Scene, name: string, color: Color3, alpha: number): StandardMaterial {
  const material = new StandardMaterial(name, scene);
  material.disableLighting = true;
  material.emissiveColor = color;
  material.diffuseColor = Color3.Black();
  material.specularColor = Color3.Black();
  material.alpha = alpha;
  material.backFaceCulling = false;
  material.zOffset = LOOK.zOffset;
  return material;
}

/** Unit disc lying on the ground (scaled per use). */
function flatDisc(scene: Scene, name: string): Mesh {
  const disc = CreateDisc(name, { radius: 1, tessellation: LOOK.shadow.tessellation }, scene);
  disc.rotation.x = Math.PI / 2;
  disc.bakeCurrentTransformIntoVertices();
  return disc;
}
